import { CalendarDotsIcon, ListMagnifyingGlassIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/common/button';
import { Chip } from '@/components/common/chip';
import { Drawer } from '@/components/feedback/drawer';
import { formatDayMonthYear } from '@/lib/date';

import { createWarrantyImageUploader } from '../api';
import { CLAIM_STATUS_CHIP, WARRANTY_COPY } from '../constants';
import {
  flattenWarrantyClaimPages,
  useEligibleDevices,
  useWarrantyClaims,
  warrantyKeys,
} from '../hooks';
import type { WarrantyClaimSummary } from '../schemas';

import { WarrantyClaimFormDrawer } from './warranty-claim-form-drawer';
import styles from './warranty-claims-drawer.module.scss';
import { WarrantyPreviewDrawer } from './warranty-preview-drawer';

/** Figma 430:394247 shows four rows; a full page of 20 placeholders would just overflow. */
const SKELETON_ROWS = 4;

export type WarrantyClaimsDrawerProps = {
  open: boolean;
  onClose: () => void;
  /**
   * The Device whose claims these are (113581 §A). **Omit for the account-wide flow** (§B), where
   * the panel lists every claim across every Device and the intake form's Device field is open to
   * selection. A Device *is* the unit here either way (ADR-0019).
   */
  customerDeviceId?: string;
  /** How the Device is named in the form's locked "Select Device" field. Device flow only. */
  deviceLabel?: string;
};

function ClaimRow({ claim, onOpen }: { claim: WarrantyClaimSummary; onOpen: () => void }) {
  const chip = CLAIM_STATUS_CHIP[claim.status];
  // `requestedAt` is a plain `z.string()` on the wire and `formatDayMonthYear` returns `null` for
  // anything dayjs cannot read, so the whole line is dropped rather than left as a bare calendar
  // glyph with no date beside it.
  const requestedAt = formatDayMonthYear(claim.requestedAt);

  return (
    <li>
      {/* The whole row is the control — the AC says selecting a claim row opens its preview, and a
          button inside the row would leave most of the row dead to the keyboard. */}
      <button type="button" className={styles.row} onClick={onOpen}>
        <div className={styles.rowTop}>
          <div className={styles.identity}>
            <span className={styles.title}>{claim.title}</span>
            <span className={styles.subtitle}>{claim.subtitle}</span>
          </div>
          <Chip intent={chip.intent}>{chip.label}</Chip>
        </div>
        {requestedAt ? (
          <div className={styles.date}>
            <CalendarDotsIcon size={16} aria-hidden />
            <span>{requestedAt}</span>
          </div>
        ) : null}
      </button>
    </li>
  );
}

/**
 * Warranty claims as a right-side slide-over — one Device's (113581 §A, ticket 02) or the whole
 * account's (113581 §B, ticket 06), decided solely by whether `customerDeviceId` is supplied.
 *
 * ADR-0019's routing rule, the rows, the empty state, the skeleton, the sentinel and the pinned CTA
 * are scope-blind by construction: only the query key, the URL and the meaning of
 * `hasEligibleDevices` differ. Row navigation is one of them — ticket 03 wired it at this
 * component's single `<ClaimRow>` site, so selecting a row opens its Ticket Preview as a second,
 * stacked drawer in **both** flows; the list is never unmounted, which is what preserves its
 * scroll position and its loaded pages.
 *
 * `<Drawer cancelText="">` suppresses the cancel slot, and with no `primaryText` `OverlayFooter`
 * renders nothing at all — which is exactly the footer-less panel Figma draws for an ineligible
 * Device.
 */
export function WarrantyClaimsDrawer({
  open,
  onClose,
  customerDeviceId,
  deviceLabel,
}: WarrantyClaimsDrawerProps) {
  const query = useWarrantyClaims(customerDeviceId, open);
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query;
  const claims = flattenWarrantyClaimPages(query.data?.pages);
  const queryClient = useQueryClient();

  // Only the account-wide flow chooses a Device, and it prefetches on panel-open rather than on
  // form-open so the dropdown is already populated on BOTH routes into the form — the direct route
  // and the CTA. No Figma frame draws a loading dropdown, and this is what keeps one unneeded.
  const eligibleDevices = useEligibleDevices(open && !customerDeviceId);

  const [selectedClaimId, setSelectedClaimId] = useState<string | null>(null);
  const [raising, setRaising] = useState(false);

  // A closed panel holds neither a selection nor a form, so reopening it lands on the list — not on
  // whichever claim was last previewed, nor on the form the Grower left. This is React's documented
  // "adjust state when a prop changes" pattern rather than an effect: the effect form renders the
  // stale value once before clearing it, and `react-hooks/set-state-in-effect` rejects a setState
  // synchronous in an effect body outright. It is the idiom
  // `features/map/components/location-results.tsx` already uses, and it sits above `formOpen` so
  // this render already reads the reset value.
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (!open) {
      setSelectedClaimId(null);
      setRaising(false);
    }
  }

  // ADR-0019's one routing rule, from the one query: an eligible Device with no claims never sees
  // a list. `hasEligibleDevices` is the server's whole eligibility surface — nothing here recomputes
  // a replacement window or looks at a Replacement request, and nothing asks *why* a Device is
  // ineligible.
  //
  // `raising` and `autoForm` are disjoint by construction: the CTA only exists when there are
  // claims, and `autoForm` only when there are none.
  //
  // ponytail: derived, not latched. A background refetch could in principle flip `autoForm` while
  // the Grower is typing, but only if this Device's first claim arrived from another surface mid-form.
  // Latch it behind a `useState` if that ever shows up in QA.
  const hasEligibleDevices = query.data?.pages[0]?.hasEligibleDevices ?? false;
  const autoForm = hasEligibleDevices && !query.isPending && !query.isError && claims.length === 0;
  const formOpen = open && (raising || autoForm);

  const uploadImage = createWarrantyImageUploader(customerDeviceId);

  // 113581: closing the panel and reopening it starts again at the first page with the latest
  // data. The query is keyed on the scope, not on `open`, so the loaded pages would otherwise
  // outlive the panel. `warrantyKeys.claims(undefined)` is the account key — the same rule, the
  // same one line, for both flows.
  useEffect(() => {
    if (open) return;
    queryClient.removeQueries({ queryKey: warrantyKeys.claims(customerDeviceId) });
  }, [customerDeviceId, open, queryClient]);

  const observerRef = useRef<IntersectionObserver | null>(null);

  // Fetch the next page as the sentinel below the last row scrolls into view. `root` is left at
  // the viewport rather than reaching for <Drawer>'s scroller: the panel body clips the sentinel,
  // and an observer against the viewport already accounts for every clipping ancestor.
  //
  // This is a **callback ref, not an effect over a `sentinelRef.current`** — and that is
  // load-bearing. React attaches refs during the commit, but a passive effect can be scheduled by
  // a render in which `hasNextPage` had already flipped true while the sentinel was not yet
  // mounted. That effect reads a null ref and bails, and because its deps are stable afterwards it
  // never runs again: the sentinel is left unobserved for the life of the panel and paging silently
  // stops. A dependency array cannot express "the node now exists"; a callback ref fires exactly
  // when it mounts, and again with `null` when it unmounts, so there is no ordering to lose.
  //
  // No `isFetchingNextPage` guard is needed: TanStack dedupes an in-flight `fetchNextPage`, and
  // once the last page has landed `getNextPageParam` returns `null` so the call is a no-op.
  const attachSentinel = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!node) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) void fetchNextPage();
        },
        { rootMargin: '200px' },
      );
      observer.observe(node);
      observerRef.current = observer;
    },
    [fetchNextPage],
  );

  function body() {
    // The closed panel owns nothing. Today `<Drawer>` hard-codes `destroyOnHidden`, so this is
    // belt-and-braces — but without it a future `forceRender` there would leave the Device Details
    // page with a permanent shimmer and a permanent `role="status"` live region.
    if (!open) return null;

    if (query.isPending) {
      return (
        <div className={styles.skeleton} role="status" aria-busy="true" aria-label="Loading claims">
          {Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <div key={index} className={styles.skeletonRow} data-testid="claim-skeleton" />
          ))}
        </div>
      );
    }

    // Not an AC of this ticket, but a failed fetch must not read as "no claims".
    if (query.isError) {
      return (
        <div className={styles.state}>
          {/* The live region is the message alone: wrapping the Retry button in it would have a
              screen reader announce the control as part of the alert. */}
          <p className={styles.stateText} role="alert">
            {WARRANTY_COPY.LOAD_FAILED}
          </p>
          <Button variant="secondary" onClick={() => void query.refetch()}>
            {WARRANTY_COPY.RETRY}
          </Button>
        </div>
      );
    }

    if (claims.length === 0) {
      return (
        <div className={styles.state}>
          <ListMagnifyingGlassIcon size={24} aria-hidden className={styles.emptyIcon} />
          <p className={styles.emptyText}>{WARRANTY_COPY.NO_CLAIMS}</p>
        </div>
      );
    }

    return (
      <>
        <ul className={styles.list}>
          {claims.map((claim) => (
            <ClaimRow key={claim.id} claim={claim} onOpen={() => setSelectedClaimId(claim.id)} />
          ))}
        </ul>
        {hasNextPage ? (
          <div ref={attachSentinel} className={styles.sentinel} data-testid="claim-sentinel" />
        ) : null}
        {isFetchingNextPage ? (
          <div className={styles.nextPage} role="status" aria-label="Loading more claims">
            <div className={styles.skeletonRow} />
          </div>
        ) : null}
      </>
    );
  }

  // Mutually exclusive, not stacked. The list->Ticket Preview pair stacks (grilling Q5) because the
  // preview has to preserve the list's scroll position; the form has no such AC, and one drawer at a
  // time needs no `mask={false}` escape hatch on the shared `<Drawer>`.
  if (formOpen) {
    return (
      <WarrantyClaimFormDrawer
        open
        // Cancel and x return the Grower to where they came from: the list if the CTA opened the
        // form, the Device (or the Profile) itself if the routing rule did.
        onClose={() => (raising ? setRaising(false) : onClose())}
        onRaised={onClose}
        // One option, preselected and locked, in the Device flow; the server's eligible list, open
        // to selection, account-wide. `defaultCustomerDeviceId` is what makes the field locked, so
        // the two flows differ by exactly these two props.
        devices={
          customerDeviceId
            ? [{ value: customerDeviceId, label: deviceLabel ?? '' }]
            : (eligibleDevices.data ?? [])
        }
        defaultCustomerDeviceId={customerDeviceId}
        onUpload={uploadImage}
      />
    );
  }

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={WARRANTY_COPY.CLAIMS_TITLE}
        cancelText=""
        // Figma 430:411822 hides the Cancel instance and leaves one pinned 191x48 primary, which is
        // exactly what an empty `cancelText` plus a `primaryText` produces.
        primaryText={hasEligibleDevices ? WARRANTY_COPY.RAISE_CTA : undefined}
        onPrimary={() => setRaising(true)}
      >
        {body()}
      </Drawer>
      {/* Stacked, not swapped in place: the list staying mounted is what preserves its scroll
          position and its already-loaded pages (grilling Q5 / ADR-0019). */}
      {selectedClaimId ? (
        <WarrantyPreviewDrawer
          open
          claimId={selectedClaimId}
          onClose={() => setSelectedClaimId(null)}
        />
      ) : null}
    </>
  );
}
